'use client'

import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import { redirect } from 'next/navigation'
import Link from 'next/link'
import Button from '@/components/ui/Button'
import Card from '@/components/ui/Card'
import Container from '@/components/ui/Container'
import { SkeletonCard } from '@/components/ui/Skeleton'
import { useToast } from '@/components/ui/ToastContainer'
import ClubMembershipButton from '@/components/ClubMembershipButton'

interface Club {
  id: string
  name: string
  description: string | null
  registrationUrl: string | null
  isPublic: boolean
  superAdmins: Array<{
    user: {
      id: string
      name: string
      email: string
    }
  }>
  admins: Array<{
    user: {
      id: string
      name: string
      email: string
    }
  }>
  events: Array<{
    id: string
    name: string
    startTime: string
    endTime: string
    isPublic: boolean
  }>
  _count: {
    events: number
  }
  accessLevel: {
    canMakePublic: boolean
    isPublic: boolean
    isClubAdmin: boolean
    isSuperAdmin: boolean
  }
}

export default function ClubsPage() {
  const { data: session, status } = useSession()
  const { showToast } = useToast()
  const [clubs, setClubs] = useState<Club[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState<'name' | 'events' | 'admins'>('name')
  const [updatingClubs, setUpdatingClubs] = useState<Set<string>>(new Set())

  useEffect(() => {
    async function fetchClubs() {
      try {
        // Always use accessible clubs API (handles both authenticated and guest users)
        const response = await fetch('/api/clubs/accessible')
        if (response.ok) {
          const data = await response.json()
          setClubs(data)
        } else {
          setError('Failed to load clubs')
        }
      } catch (err) {
        setError('Failed to load clubs')
      } finally {
        setLoading(false)
      }
    }

    fetchClubs()
  }, [session]) // Reload when session changes

  async function toggleClubPublic(clubId: string, currentStatus: boolean) {
    setUpdatingClubs(prev => new Set(prev).add(clubId))
    try {
      const response = await fetch(`/api/clubs/${clubId}/public`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isPublic: !currentStatus })
      })

      if (response.ok) {
        const data = await response.json()
        showToast({
          type: 'success',
          title: 'Club Updated',
          message: data.message
        })
        // Reload clubs to get updated data
        const refreshResponse = await fetch('/api/clubs/accessible')
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json()
          setClubs(refreshData)
        }
      } else {
        const data = await response.json()
        const statusCode = response.status
        const errorMessage = data.error || 'Failed to update club'
        const detailedError = `Status: ${statusCode} - ${errorMessage}${data.details ? ` (${data.details})` : ''}`
        
        console.error('Toggle club public error:', {
          status: statusCode,
          error: data.error,
          details: data.details,
          fullResponse: data
        })
        
        showToast({
          type: 'error',
          title: 'Failed to Update Club',
          message: detailedError,
          duration: 8000,
        })
      }
    } catch (error) {
      console.error('Toggle club public error:', error)
      const errorMessage = `Network/Client Error: ${error instanceof Error ? error.message : 'Unknown error'}`
      showToast({
        type: 'error',
        title: 'Failed to Update Club',
        message: errorMessage,
        duration: 8000,
      })
    } finally {
      setUpdatingClubs(prev => {
        const newSet = new Set(prev)
        newSet.delete(clubId)
        return newSet
      })
    }
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-ocean-600 to-teal-600 text-white shadow-lg">
          <Container className="py-6">
            <div className="h-10 w-64 bg-white/20 rounded animate-pulse mb-2" />
            <div className="h-5 w-96 bg-white/20 rounded animate-pulse" />
          </Container>
        </div>
        <Container className="py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </Container>
      </div>
    )
  }

  // Remove authentication requirement - page is now accessible to guests

  const canCreateClub = session && ['MEMBER', 'ADMIN', 'SUPER_ADMIN'].includes(session.user.role)

  // Filter and sort clubs
  const filteredAndSortedClubs = clubs
    .filter(club => {
      if (!searchQuery) return true
      const query = searchQuery.toLowerCase()
      return (
        club.name.toLowerCase().includes(query) ||
        (club.description?.toLowerCase() || '').includes(query)
      )
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'events':
          return b._count.events - a._count.events
        case 'admins':
          return (b.superAdmins.length + b.admins.length) - (a.superAdmins.length + a.admins.length)
        default:
          return 0
      }
    })

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-ocean-600 to-teal-600 text-white shadow-lg">
        <Container className="py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold mb-1">Fishing Clubs</h1>
              <p className="text-ocean-100">Browse and join fishing clubs in your area</p>
            </div>
            <div className="flex gap-3">
              {canCreateClub && (
                <Button href="/clubs/new" variant="secondary" size="sm">
                  + Create Club
                </Button>
              )}
              {session ? (
                <Button href="/dashboard" variant="secondary" size="sm">
                  Back to Dashboard
                </Button>
              ) : (
                <Button href="/auth/signin" variant="secondary" size="sm">
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </Container>
      </div>

      <Container className="py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Search and Filter Bar */}
        {clubs.length > 0 && (
          <div className="mb-6 flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search clubs by name or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'name' | 'events' | 'admins')}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ocean-500 focus:border-transparent transition-all bg-white"
            >
              <option value="name">Sort by Name</option>
              <option value="events">Sort by Events</option>
              <option value="admins">Sort by Admins</option>
            </select>
          </div>
        )}

        {/* Results count */}
        {clubs.length > 0 && (
          <div className="mb-4 text-sm text-gray-600">
            {searchQuery ? (
              <span>
                Found <strong>{filteredAndSortedClubs.length}</strong> of <strong>{clubs.length}</strong> clubs
              </span>
            ) : (
              <span>
                Showing <strong>{clubs.length}</strong> club{clubs.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : filteredAndSortedClubs.length === 0 && searchQuery ? (
          <Card>
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No clubs found</h3>
              <p className="text-gray-600 mb-6">
                No clubs match &quot;{searchQuery}&quot;
              </p>
              <Button variant="secondary" onClick={() => setSearchQuery('')}>
                Clear Search
              </Button>
            </div>
          </Card>
        ) : clubs.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏛️</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No clubs yet</h3>
              <p className="text-gray-600 mb-6">
                {canCreateClub
                  ? 'Be the first to create a fishing club!'
                  : session 
                    ? 'Check back soon for new clubs.'
                    : 'Check back soon for new clubs. Sign in to create a club!'}
              </p>
              {canCreateClub && (
                <Button href="/clubs/new">Create First Club</Button>
              )}
              {!session && (
                <Button href="/auth/signin" variant="secondary">
                  Sign In to Create Club
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAndSortedClubs.map((club) => {
              const isClubAdmin = session && (
                club.superAdmins.some(
                  (sa) => sa.user.id === session.user.id
                ) || club.admins.some(
                  (a) => a.user.id === session.user.id
                ) || session.user.role === 'SUPER_ADMIN'
              )

              return (
                <Card key={club.id} className="hover:shadow-lg transition-shadow">
                  <div className="flex flex-col h-full">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{club.name}</h3>
                        <div className="flex flex-col gap-1">
                          {!club.accessLevel?.isPublic && (
                            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full">
                              🔒 Private
                            </span>
                          )}
                          {isClubAdmin && (
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-semibold rounded-full">
                              Admin
                            </span>
                          )}
                        </div>
                      </div>
                      {club.description && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                          {club.description}
                        </p>
                      )}
                    </div>
                    <div className="border-t pt-4 mt-4">
                      <div className="flex gap-4 text-sm text-gray-500 mb-4">
                        <span>📅 {club._count.events} events</span>
                        <span>
                          👥 {club.superAdmins.length + club.admins.length} admins
                        </span>
                      </div>
                      <div className="space-y-2">
                        {isClubAdmin && (
                          <Button
                            href={`/admin/clubs/${club.id}`}
                            size="sm"
                            className="w-full"
                          >
                            Manage Club
                          </Button>
                        )}
                        {session && club.accessLevel?.canMakePublic && (
                          <Button
                            onClick={() => toggleClubPublic(club.id, club.accessLevel.isPublic)}
                            size="sm"
                            className={`w-full ${
                              club.accessLevel.isPublic 
                                ? 'bg-gray-600 hover:bg-gray-700' 
                                : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                            disabled={updatingClubs.has(club.id)}
                          >
                            {updatingClubs.has(club.id) ? 'Updating...' : 
                             club.accessLevel.isPublic ? 'Make Private' : 'Make Public'}
                          </Button>
                        )}
                        {!isClubAdmin && (
                          <ClubMembershipButton 
                            clubId={club.id} 
                            clubName={club.name}
                            registrationUrl={club.registrationUrl}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </Container>
    </div>
  )
}

